import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios, { AxiosError } from "axios";
import type { AuthContextValue, UserProfile } from "../types";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://engineeringofstockbackend.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

const EOSAuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useEOSAuth = () => {
  const context = useContext(EOSAuthContext);
  if (!context) {
    throw new Error("useEOSAuth must be used within an EOSAuthProvider");
  }
  return context;
};

export const EOSAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(
    typeof window !== "undefined" ? window.localStorage.getItem("eos_token") : null,
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  const logout = useCallback(async () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("eos_token");
    }
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    delete api.defaults.headers.common.Authorization;
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get("/api/auth/verify");
      setUser((response.data?.data?.user as UserProfile) ?? null);
      setIsAuthenticated(true);
    } catch {
      await logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      void checkAuthStatus();
    } else {
      setIsLoading(false);
    }
  }, [token, checkAuthStatus]);

  const register = useCallback(
    async (userData: { email: string; password: string; firstName: string; lastName: string }) => {
      try {
        setError(null);
        setIsLoading(true);
        const response = await api.post("/api/auth/register", userData);
        const newToken = response.data?.data?.token as string;
        const newUser = response.data?.data?.user as UserProfile;

        if (typeof window !== "undefined") {
          window.localStorage.setItem("eos_token", newToken);
        }
        setToken(newToken);
        setUser(newUser);
        setIsAuthenticated(true);
      } catch (err) {
        const errorMessage = (err as AxiosError<{ message?: string }>).response?.data?.message || "Registration failed";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const login = useCallback(async (credentials: { email: string; password: string }) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await api.post("/api/auth/login", credentials);
      const newToken = response.data?.data?.token as string;
      const newUser = response.data?.data?.user as UserProfile;

      if (typeof window !== "undefined") {
        window.localStorage.setItem("eos_token", newToken);
      }
      setToken(newToken);
      setUser(newUser);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage = (err as AxiosError<{ message?: string }>).response?.data?.message || "Login failed";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateBroker = useCallback(async (broker: string) => {
    try {
      setError(null);
      const response = await api.put("/api/auth/broker", { broker });
      setUser((prev) =>
        prev
          ? { ...prev, broker: response.data?.data?.broker, brokerConnected: response.data?.data?.brokerConnected }
          : prev,
      );
    } catch (err) {
      const errorMessage =
        (err as AxiosError<{ message?: string }>).response?.data?.message || "Failed to update broker";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const updateProfile = useCallback(async (profileData: Record<string, unknown>) => {
    try {
      setError(null);
      const response = await api.put("/api/auth/profile", profileData);
      setUser((prev) => (prev ? { ...prev, ...response.data?.data } : prev));
    } catch (err) {
      const errorMessage =
        (err as AxiosError<{ message?: string }>).response?.data?.message || "Failed to update profile";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      updateBroker,
      updateProfile,
      clearError,
    }),
    [user, token, isAuthenticated, isLoading, error, login, register, logout, updateBroker, updateProfile, clearError],
  );

  return <EOSAuthContext.Provider value={value}>{children}</EOSAuthContext.Provider>;
};
